<template>
  <div class="row mt-2 mb-3">
    <div class="col-5 order-setupitem-title-area">
      <h4>External Setup</h4>
    </div>
    <div class="col-6">
      <div v-if="userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderApproval) && !isOrderOnHold && available && !isPurchaseOrdersCreated">
        <ProgressTrackerButton @click="$emit('setup')" icon="fa-dolly-flatbed">Setup External Items</ProgressTrackerButton>
      </div>
      <p class="mt-1" v-if="(!userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderApproval) || isOrderOnHold) && available">External Setup required</p>
      <div>
        <div v-if="isExternalSetupComplete && isPurchaseOrdersCreated">
          <p class="order-setupitem-result status-approved"><i class="far fa-check-circle"></i> Purchase Orders Sent</p>
          <slot name="approved" />
        </div>

        <p class="order-setupitem-result status-pending" v-if="purchaseOrderPendingSentList.length > 0 && !isOrderOnHold && available">
          <i class="far fa-envelope"></i> {{purchaseOrderPendingSentList.length}} Purchase Order{{purchaseOrderPendingSentList.length | displayCountPlural}} waiting to be sent
        </p>

        <div class="order-panel-list" v-if="validPurchaseOrderList.length > 0">
          <div class="font-weight-bold mt-2">Current Purchase Orders</div>
          <ItemPanelList class="mt-2">
            <ItemPanel v-for="purchaseOrder of validPurchaseOrderList" :key="purchaseOrder.id" icon="fa-dolly-flatbed">
              <template v-slot:title>
                <strong>
                  <LinkPurchaseOrder :id="purchaseOrder.id" name="Purchase Order" />
                </strong>
              </template>
              <template v-slot:description>
                 {{purchaseOrder.sequenceNumber}}
              </template>
              <template v-slot:content>
                <ItemPanelData title="Status" v-if="(purchaseOrder.isSupplierSent && purchaseOrder.status != PurchaseOrderStatusTypes.cancelled) || purchaseOrder.status == PurchaseOrderStatusTypes.cancelled">
                  <LabelBadge :labelClass="`label-po-status-${purchaseOrder.status}`">{{purchaseOrder.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Date">
                  {{purchaseOrder.createdAt | formatDateTime}}
                </ItemPanelData>
                <ItemPanelData title="Sent" v-if="purchaseOrder.isSupplierSent && purchaseOrder.status != PurchaseOrderStatusTypes.cancelled">
                  <Icon icon="fa-check" iconStyle="fas" iconClass="status-approved" />
                  <p>Sent at {{purchaseOrder.supplierDateSent | formatDateTime}}</p>
                </ItemPanelData>
                <ItemPanelData title="Send" v-if="(!purchaseOrder.isSupplierSent || purchaseOrder.status == PurchaseOrderStatusTypes.cancelled) && available && userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderApproval)">
                  <ProgressTrackerButton @click="$emit('emailPurchaseOrder', purchaseOrder.id)" icon="fa-envelope">Email</ProgressTrackerButton>
                </ItemPanelData>
              </template>
            </ItemPanel>
          </ItemPanelList>
        </div>
        <div class="order-panel-list" v-if="cancelledPurchaseOrderList.length > 0">
          <div class="font-weight-bold mt-2">Cancelled Purchase Orders</div>
          <ItemPanelList class="mt-2">
            <ItemPanel v-for="purchaseOrder of cancelledPurchaseOrderList" :key="purchaseOrder.id" icon="fa-dolly-flatbed">
              <template v-slot:title>
                <strong>
                  <LinkPurchaseOrder :id="purchaseOrder.id" name="Purchase Order" />
                </strong>
              </template>
              <template v-slot:description>
                 {{purchaseOrder.sequenceNumber}}
              </template>
              <template v-slot:content>
                <ItemPanelData title="Status" v-if="(purchaseOrder.isSupplierSent && purchaseOrder.status != PurchaseOrderStatusTypes.cancelled) || purchaseOrder.status == PurchaseOrderStatusTypes.cancelled">
                  <LabelBadge :labelClass="`label-po-status-${purchaseOrder.status}`">{{purchaseOrder.status | formatLookupName(PurchaseOrderStatusTypeLookup)}}</LabelBadge>
                </ItemPanelData>
                <ItemPanelData title="Date">
                  {{purchaseOrder.createdAt | formatDateTime}}
                </ItemPanelData>
                <ItemPanelData title="Sent" v-if="purchaseOrder.isCancelledSent">
                  <Icon icon="fa-check" iconStyle="fas" iconClass="status-approved" />
                  <p>Sent at {{purchaseOrder.cancelledDateSent | formatDateTime}}</p>
                </ItemPanelData>
                <ItemPanelData title="Send Cancellation" v-if="!purchaseOrder.isCancelledSent && available && userHasUpdatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderApproval)">
                  <ProgressTrackerButton @click="$emit('emailPurchaseOrder', purchaseOrder.id)" icon="fa-envelope">Email</ProgressTrackerButton>
                </ItemPanelData>
              </template>
            </ItemPanel>
          </ItemPanelList>
        </div>

        <!-- Shouldn't get here. If all purchase orders are ready, the order shouldn't be pending external setup -->
        <div v-if="!isExternalSetupComplete && isPurchaseOrdersCreated && allPurchaseOrdersReady && available" class="mt-2">
          <p class="order-setupitem-result status-approved"><i class="far fa-exclamation-circle"></i> Purchase Orders Ready.</p>
        </div>
      </div>
      
      <slot name="pending" />
    </div>
    <div class="col-1"> </div>
  </div>
</template>

<script>
import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import { ApprovalTypes, OrderApprovalTypes, OrderStatusTypes, PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup } from '@/store/types'

export default {
  name: 'OrderExternalSetupBar',
  components: {
    ProgressTrackerButton
  },
  props: {
    available: {
      type: Boolean,
      default: false
    },
    isExternalSetupComplete: {
      type: Boolean,
      default: false
    },
    order: Object,
    orderItem: Object,
    approveButtonTitle: {
      type: String,
      default: 'Approve'
    },
    denyButtonTitle: {
      type: String,
      default: 'Deny'
    }
  },
  created() {
    this.ApprovalTypes = ApprovalTypes
    this.OrderApprovalTypes = OrderApprovalTypes
    this.PurchaseOrderStatusTypes = PurchaseOrderStatusTypes
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
  },
  computed: {
    purchaseOrderList() {
      if(!this.order || !this.order.purchaseOrders)
        return []
      return this.order.purchaseOrders
    },
    validPurchaseOrderList() {
      return this.purchaseOrderList.filter(purchaseOrder => purchaseOrder.status != PurchaseOrderStatusTypes.cancelled)
    },
    cancelledPurchaseOrderList() {
      return this.purchaseOrderList.filter(purchaseOrder => purchaseOrder.status == PurchaseOrderStatusTypes.cancelled)
    },
    purchaseOrderPendingSentList() {
      return this.validPurchaseOrderList.filter(purchaseOrder => !purchaseOrder.isSupplierSent)
    },
    isPurchaseOrdersCreated() {
      return this.validPurchaseOrderList.filter(purchaseOrder => purchaseOrder.status != PurchaseOrderStatusTypes.cancelled).length > 0
    },
    allPurchaseOrdersReady() {
      if(this.validPurchaseOrderList.filter(purchaseOrder => purchaseOrder.isSupplierSent || purchaseOrder.status == PurchaseOrderStatusTypes.receiving || purchaseOrder.status == PurchaseOrderStatusTypes.completed).length == this.validPurchaseOrderList.length)
        return true
      return false
    },

    approvalUserName() {
      let approval = this.getApprovalInfoForType()
      if(approval && approval.user)
        return approval.user.name
      return ''
    },
    approvalDate() {
      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.approvalUpdatedDate
      return ''
    },
    approvalRequestReasonList() {
      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.requestReason.split('\n')
      return []
    },
    approvedNote() {
      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.approvedNote
      return ''
    },
    deniedReason() {
      let approval = this.getApprovalInfoForType()
      if(approval) {
        var deniedReason = ''
        deniedReason += approval.deniedReason

        return deniedReason
      }
      return ''
    },
    isStatusExternalSetup() {
      return this.order.status == OrderStatusTypes.pendingExternalSetup
    },
    isOrderOnHold() {
      return this.order.isOnHold
    }
  },
  methods: {
    clickedApprovalReason(event) {
      this.$emit('displayDetails', event)
    },
    showPurchaseOrder(purchaseOrderId) {
      this.openLinkNewTab(`/purchasing/purchaseorders/${purchaseOrderId}`)
    }
  }
}
</script>